import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { ValuationData, ValuationMetrics } from '@/types/valuation';

export const useValuation = () => {
  return useQuery({
    queryKey: ['valuation'],
    queryFn: async (): Promise<ValuationData> => {
      console.log('Fetching valuation data...');
      
      const { data, error } = await supabase.functions.invoke('valuation-data');
      
      if (error) {
        console.error('Valuation data error:', error);
        throw new Error(`Failed to fetch valuation data: ${error.message}`);
      }
      
      console.log('Valuation data received:', data?.items?.length || 0, 'assets');
      
      // Asset filtering lists (fail-safe)
      const STABLECOIN_IDS = new Set([
        'tether', 'usd-coin', 'dai', 'frax', 'usdd', 'usdp', 'fdusd', 'tusd', 'gusd', 
        'usde', 'usds', 'pyusd', 'usdn', 'usdx', 'ethena-usde', 'ethena-staked-usde',
        'binance-bridged-usdt-bnb-smart-chain', 'usdt', 'usdc'
      ]);

      const WRAPPED_TOKEN_IDS = new Set([
        'wrapped-bitcoin', 'weth', 'wrapped-beacon-eth', 'wrapped-steth', 'staked-ether',
        'coinbase-wrapped-btc', 'wrapped-steth', 'jito-staked-sol', 'wrapped-eeth',
        'binance-bridged-usdt-bnb-smart-chain'
      ]);

      const MEMECOIN_IDS = new Set([
        'dogecoin', 'shiba-inu', 'pepe', 'dogwifhat', 'floki', 'bonk', 'baby-doge-coin',
        'milady-meme-coin', 'brett-based'
      ]);

      const shouldExcludeAsset = (asset: any): boolean => {
        const id = asset.slug?.toLowerCase() || '';
        const name = asset.name?.toLowerCase() || '';
        const symbol = asset.symbol?.toLowerCase() || '';

        // Check explicit lists
        if (STABLECOIN_IDS.has(id) || WRAPPED_TOKEN_IDS.has(id) || MEMECOIN_IDS.has(id)) {
          return true;
        }

        // Stablecoin heuristics
        if (/(usd|usdt|usdc|dai|tusd|usdp|gusd|usdd|usde|usds|pyusd)$/i.test(symbol) ||
            name.includes('usd') && !name.includes('liquid')) {
          return true;
        }

        // Wrapped token heuristics
        if (id.startsWith('wrapped-') || name.includes('wrapped') || 
            name.includes('staked') && name.includes('eth')) {
          return true;
        }

        return false;
      };

      // Apply fail-safe filtering
      if (data?.items) {
        data.items = data.items.filter((asset: any) => !shouldExcludeAsset(asset)).slice(0, 400);
      }
      
      return data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchInterval: 5 * 60 * 1000, // Auto-refresh every 5 minutes
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

export const useValuationAsset = (slug: string) => {
  return useQuery({
    queryKey: ['valuation', slug],
    queryFn: async (): Promise<ValuationMetrics | null> => {
      const { data, error } = await supabase.functions.invoke('valuation-data');
      
      if (error) {
        throw new Error(`Failed to fetch valuation data: ${error.message}`);
      }
      
      return data?.items?.find((item: ValuationMetrics) => item.slug === slug) || null;
    },
    staleTime: 5 * 60 * 1000,
    enabled: !!slug,
  });
};